function objectFlip(obj) {
  return Object.keys(obj).reduce((ret, key) => {
    ret[obj[key]] = key;
    return ret;
  }, {});
}

export class SpodError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "SpodError";
    this.code = `E-${code}`;

    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}

export class PrepareToPlayOverlay extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "PrepareToPlayOverlay";
    this.reason = PREPARE_TO_PLAY_OVERLAY_REASONS[code];
  }
}

export class PlaybackError extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "PlaybackError";
    this.code = `P-${code}`;
    this.reason = PLAYBACK_ERROR_REASONS[code];
    if (PLAYBACK_ERROR_MESSAGES[code]) {
      this.message = PLAYBACK_ERROR_MESSAGES[code];
    }
  }
}

export class PlaybackSessionError extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "PlaybackSessionError";
    this.code = `PS-${code}`;
  }
}

export class GeoLocationError extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "GeoLocationError";
    this.code = `G-${code}`;
    if (!message) {
      this.message = GEOLOCATION_ERROR_MESSAGES[code];
    }
  }
}

export class AdultNotViewableError extends SpodError {
  constructor(message, code = "500") {
    super(message, code);
    this.name = "AdultNotViewableError";
    this.code = `A-${code}`;
    if (!message) {
      this.message =
        "この動画はIOSバージョン11.2未満では再生できません。申し訳ありませんが、別デバイスにてご利用ください。";
    }
  }
}

export class StreaksPlayerError extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "StreaksPlayerError";
    this.code = `S-${code}`;
    this.message =
      MEDIA_ERROR_MESSAGES[code] ||
      PLAYER_ERROR_MESSAGES[code] ||
      message ||
      WATCH_ERROR_MESSAGES[0];
  }
}

export class PlalaBeaconError extends SpodError {
  constructor(message, code) {
    super(message, code);
    this.name = "PlalaBeaconError";
    this.code = `PL-${code}`;
    this.message =
      "エラーが発生しました。大変お手数おかけしますが、しばらくたってからもう一度お試しください。";
  }
}

export const PREPARE_TO_PLAY_OVERLAY_REASONS = {
  1000: "needlogin",
  1001: "needlogin_and_contract",
  1002: "entitlement_confirm",
  1003: "resume",
  1004: "live_before",
  1005: "dvr_live",
  1006: "appkick",
  1007: "rating",
  1008: "suggest_app",
  1009: "chat_select",
};

export const PREPARE_TO_PLAY_OVERLAY_CODES = objectFlip(
  PREPARE_TO_PLAY_OVERLAY_REASONS
);

export const PLAYBACK_ERROR_REASONS = {
  // 以下playbackAPIで返ってくるエラー
  2000: "meta_notfound",
  2002: "meta_forbidden",
  2003: "meta_not_delivering",
  2020: "media_notfound",
  2021: "media_forbidden",
  2030: "file_nodfound",
  2031: "file_forbidden",
  2040: "unauthorized",
  2041: "session_invalid",
  2042: "device_invalid",
  2043: "meta_geo_forbidden",
  2050: "inuse_viewing_device_limit",
  2051: "inuse_same_meta_limit",
  2052: "media_access_denied",
  2053: "media_geo_forbidden",
  2054: "publish_rule_forbidden",
  2055: "delivery_rule_forbidden",
  2056: "not_entitled",
  2057: "activated_viewing_device_limit",
  2058: "not_delivery_started",
  2059: "not_delivering",
  2060: "meta_not_published",
  2061: "invalid_playback_session_id",
  2062: "agreement_required",
  2063: "playback_engine_unauthorized",
  2064: "playback_session_id_is_not_renewable",
  2065: "entitlement_suspend",
  2066: "entitlement_suspend_recurring_failed",
  2067: "rating_limit_over",
  2068: "meta_has_no_rights",
  2069: "end_at_changes_warning",
  2070: "not_started_entitlement",
  2999: "other",
};

export const PLAYBACK_ERROR_CODES = objectFlip(PLAYBACK_ERROR_REASONS);

export const PLAYBACK_ERROR_MESSAGES = {
  2000: "ページが見つかりませんでした。このページは公開終了しています。",
  2003: "現在配信されておりません。配信期間をご確認ください。",
  2020: "再生に失敗しました。このエピソードは配信終了しています。",
  2041: "お客様のアカウントでは再生ができません。視聴可能なアカウントでログインして再生をお試しください。",
  2042: "再生に失敗しました。ログインしなおすか、時間をおいて再度お試しください。",
  2050: "別端末で再生中です。同時に同じ動画が視聴されているか、同時に視聴可能な端末数(3台)が上限に達しています。",
  2051: "この動画はすでに同じアカウントの別端末にて視聴されています。同じ動画を同時に視聴することは許可されていません。",
  2054: "お客様の地域またはアプリではご利用いただけません。国内からアクセスいただいている場合はお問い合わせページよりお問い合わせをお願いします。",
  2055: "お客様の地域またはアプリではご利用いただけません。国内からアクセスいただいている場合はお問い合わせページよりお問い合わせをお願いします。",
  2061: "再生に失敗しました。ログインしなおすか、時間をおいて再度お試しください。",
  2063: "再生に失敗しました。ログインしなおすか、時間をおいて再度お試しください。",
  2064: "再生に失敗しました。ログインしなおすか、時間をおいて再度お試しください。",
  2065: "この動画は一時的に再生できません。お問い合わせページよりお問い合わせをお願いします。",
  2066: "契約更新に失敗したため、ご利用を一時的に停止しています。",
  2067: "年齢制限のため、この作品を再生することができません。プロフィール設定から年齢制限を変更いただくか、別のプロフィールからご視聴をお願いします。",
  2068: "この動画は一時的に再生できません。お問い合わせページよりお問い合わせをお願いします。",
  2069: "この動画は一時的に再生できません。お問い合わせページよりお問い合わせをお願いします。",
  2999: "再生に失敗しました。ログインしなおすか、時間をおいて再度お試しください。",
};

export const PLAYBACK_SESSION_ERROR_REASONS = {
  3001: "concurrent_streams_limit",
  3002: "invalid_grant",
  3999: "other",
};

export const PLAYBACK_SESSION_ERROR_CODES = objectFlip(
  PLAYBACK_SESSION_ERROR_REASONS
);

export const PLAYBACK_SESSION_ERROR_MESSAGES = {
  3001: "別端末で再生中です。同時に同じ動画が視聴されているか、同時に視聴可能な端末数(3台)が上限に達しています。",
  3002: "エラーが発生しました。大変お手数おかけしますが、しばらくたってからもう一度お試しください。",
  3999: "エラーが発生しました。大変お手数おかけしますが、しばらくたってからもう一度お試しください。",
};

// 位置情報系のエラー
export const GEOLOCATION_ERROR_REASONS = {
  0: "geolocation_unusable",
  1: "geolocation_permission_denied",
  2: "geolocation_position_unavailable",
  3: "geolocation_timeout",
  99: "geolocation_error",
};

export const GEOLOCATION_ERROR_CODES = objectFlip(GEOLOCATION_ERROR_REASONS);

export const GEOLOCATION_ERROR_MESSAGES = {
  0: "位置情報の利用ができませんでした。",
  1: "位置情報の利用が許可されていません。ブラウザの設定をご確認の上、ご利用ください。",
  2: "現在位置が取得できませんでした。ご利用状況をご確認の上、しばらく経ってから電波の良い場所でご利用ください。",
  3: "位置情報の取得がタイムアウトしました。ご利用状況をご確認の上、しばらく経ってから電波の良い場所でご利用ください。",
  99: "位置情報の取得でエラーが発生しました",
};

export const GEOLOCATION_ERROR_MESSAGES_IOS = Object.assign(
  {},
  GEOLOCATION_ERROR_MESSAGES,
  {
    1: " 端末の位置情報が許可されていません。設定>プライバシー>位置情報サービスをONにし、Safariの位置情報を許可してください。",
  }
);

export const GEOLOCATION_ERROR_MESSAGES_ANDROID = Object.assign(
  {},
  GEOLOCATION_ERROR_MESSAGES,
  {
    1: "端末の位置情報が許可されていません。設定から位置情報をONにし、アプリ>利用ブラウザ>権限>現在地をONにしてください。",
  }
);

export const MEDIA_ERROR_REASONS = {
  1: "media_error_aborted",
  2: "media_error_network",
  3: "media_error_decode",
  4: "media_error_src_not_supported",
  5: "media_error_encrypted",
};

export const MEDIA_ERROR_CODES = objectFlip(MEDIA_ERROR_REASONS);

export const MEDIA_ERROR_MESSAGES = {
  1: "再生を停止しました。ネットワークの状態をご確認いただくか、時間をおいて再度お試しください。",
  2: "ネットワークエラーにより動画のダウンロードが途中で失敗しました。ネットワークの状態をご確認いただくか、時間をおいて再度お試しください。",
  3: "ご利用の環境では再生ができません。再読み込みで解消しない場合は、別のブラウザでお試しください。",
  4: "ご利用の環境では再生ができません。再読み込みで解消しない場合は、別のブラウザでお試しください。",
  5: "著作権保護のためHDCP非対応のケーブル・モニタではご視聴できません。HDCP対応のケーブル・モニタをご使用して、ご視聴してください。",
};

export const PLAYER_ERROR_MESSAGES = {
  // RESTRICTIONS_CANNOT_BE_MET https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
  4012: "動画の制限により再生できません。動画のライセンスに問題が起きているか、HDCP非対応のケーブル・モニタを使用している可能性があります。",
};

// https://github.com/shaka-project/shaka-player/blob/main/lib/util/error.js
// https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
// https://github.com/shaka-project/shaka-player/blob/main/lib/util/error.js
export const PLAYER_ERROR_CODES = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  1000,
  1001,
  1002,
  1003,
  1004,
  1006,
  1007,
  1008,
  1009,
  1010,
  1101,
  1102,
  1103,
  1104,
  1105,
  1106,
  1107,
  1113,
  1400,
  2000,
  2001,
  2002,
  2003,
  2004,
  2005,
  2006,
  2007,
  2008,
  2009,
  2010,
  2011,
  2012,
  2013,
  2014,
  2015,
  2016,
  2017,
  3000,
  3001,
  3002,
  3003,
  3004,
  3005,
  3006,
  3007,
  3008,
  3009,
  3010,
  3011,
  3012,
  3013,
  3014,
  3015,
  3016,
  3017,
  3018,
  3019,
  4000,
  4001,
  4002,
  4003,
  4004,
  4005,
  4006,
  4007,
  4008,
  4009,
  4010,
  4011,
  4012,
  4013,
  4014,
  4015,
  4016,
  4017,
  4018,
  4019,
  4020,
  4021,
  4022,
  4023,
  4024,
  4025,
  4026,
  4027,
  4028,
  4029,
  4030,
  4031,
  4032,
  4033,
  4034,
  4035,
  4036,
  4037,
  4038,
  4039,
  4040,
  4041,
  4042,
  4044,
  4045,
  5006,
  6000,
  6001,
  6002,
  6003,
  6004,
  6005,
  6006,
  6007,
  6008,
  6010,
  6012,
  6013,
  6014,
  6015,
  6016,
  6017,
  6018,
  6019,
  7000,
  7001,
  7002,
  7003,
  7004,
  8000,
  8001,
  8002,
  8003,
  8004,
  8005,
  8006,
  9000,
  9001,
  9002,
  9003,
  9004,
  9005,
  9007,
  9008,
  9011,
  9012,
  9013,
  9014,
  9015,
  9016,
  9017,
  10000,
  10001,
  10002,
  10003,
  10004,
];

export const AUTH_ERROR_MESSAGES = {
  notauthenticated:
    "ご利用可能なログイン情報が確認できません。再度ログインください。",
  unauthorized:
    "ID連携できませんでした。メールアドレス、パスワードを正しく入力してください。※入力間違いが一定回数を超えると、一時的にアカウントがロックされます。",
  invalidPasscode: "暗証番号が違います",
  pincode: "認証コードの有効期限が切れているか、コードが正しくありません。",
  signup: "エラーが発生しました。登録内容をご確認の上再度お試しください。",
  duplicate:
    "ご入力のアドレスは既にSPOOXでのご登録があるため、利用登録ができませんでした。前の画面からSPOOX IDとスカパー！メンバーズIDを連携を行ってください。ヘルプセンターにてご確認ください。",
  acountlock:
    "ID連携できませんでした。メールアドレス、パスワードを正しく入力してください。※入力間違いが一定回数を超えると、一時的にアカウントがロックされます。",
  notfound:
    "ID連携できませんでした。メールアドレス、パスワードを正しく入力してください。※入力間違いが一定回数を超えると、一時的にアカウントがロックされます。\n当エラーが続く場合はヘルプセンターにてご確認ください。(002)",
  cooperated: "ID連携できませんでした。ヘルプセンターにてご確認ください。(001)",
  passwordToken: "リセットトークンが無効になっているか、正しくありません。",
  default:
    "エラーが発生しました。大変お手数おかけしますが、しばらくたってからもう一度お試しください。",
};

export const TERMS_ERROR_MESSAGES = {
  optOut:
    "オプトアウト済みのため、メール規約に同意できません。メール規約に同意する場合はアカウント画面からお手続きをお願いします。",
};

export const WATCH_ERROR_REASONS = {
  0: "other_error",
  1: "player_undefined_error",
  2: "episode_not_viewable",
  3: "video_not_published",
  4: "network_error",
  5: "drm_error",
  6: "contract_not_allowed",
};

export const WATCH_ERROR_CODES = objectFlip(WATCH_ERROR_REASONS);

export const WATCH_ERROR_MESSAGES = {
  0: "SPOOXの動画配信サービスでエラーが発生している可能性があります。しばらくしてからご利用ください。",
  1: "プレイヤーが初期化されていません。",
  2: "このエピソードは現在視聴できません、しばらく経ってからご利用ください。",
  3: "指定されたビデオを視聴することが出来ません。お使いのデバイスではこのビデオは閲覧できない可能性がございます。詳しくはSPOOXのサイトからお問い合わせください。",
  4: "動画のダウンロードに失敗しました。ネットワーク状況をご確認の上、しばらくしてからご利用ください。",
  5: "再生できません。ブラウザの著作権保護機能（ＤＲＭ）の設定を確認してください。",
  6: "この作品をご視聴いただくために必要な月額商品は契約可能期間外です。",
};

export const SUBSCRIPTION_ESTIMATE_ERROR_MESSAGES = {
  22000: "正しいクーポンコードを入力してください。",
  22001: "正しいクーポンコードを入力してください。",
  22002: "正しいクーポンコードを入力してください。",
  22003: "正しいクーポンコードを入力してください。",
  22005: "正しいクーポンコードを入力してください。",
  34001: "クーポンが適用できない商品です。",
  18003: "選択した支払い方法では契約できません。",
  18001: "すでに契約済みです。",
  18002: "現在のユーザーでは契約できません。",
  34101: "入力いただいたクーポンの割引より、すでにお客様に適用されている割引の方が割引額が大きいため、適用することができません。",
  34102: "正しいクーポンコードを入力してください。",
  34103: "入力いただいたクーポンの割引後の価格が、通常価格より大きいため、適用することができません。",
  34104: "キャンペーンコードを入力いただきましたが、トライアル終了時点でキャンペーン期間が終了している為、トライアル終了後は通常月額料金での契約継続となります。",
};

export const CAMPAIGN_CODE_ERROR_REASONS = {
  invalid_term: {
    message: "正しいクーポンコードを入力してください。",
    code: "34001-1",
  },
  over_code_user_max_count: {
    message: "このクーポンコードは使用済みです。",
    code: "34001-2",
  },
  over_user_max_count: {
    message: "このクーポンコードは使用済みです。",
    code: "34001-3",
  },
  already_entried: {
    message: "適用済みのクーポンコードです。",
    code: "34001-4",
  },
  already_joined: {
    message: "適用済みのクーポンコードです。",
    code: "34001-5",
  },
  invalid_payment_method: {
    message: "選択した支払い方法では契約できません。",
    code: "34001-6",
  },
  not_publish: {
    message: "正しいクーポンコードを入力してください。",
    code: "34001-7",
  },
  entry_not_accepted: {
    message: "正しいクーポンコードを入力してください。",
    code: "34001-8",
  },
};

export const PURCHASE_ESTIMATE_ERROR_MESSAGES = {
  21000: "正しいクーポンコードを入力してください。", // ユーザーが不正
  22000: "正しいクーポンコードを入力してください。", // 存在しない
  22001: "正しいクーポンコードを入力してください。", // /voucher_codes/{code}/checkの場合はLogicaのバグのためこのコードはこないでnullになっている
  22002: "正しいクーポンコードを入力してください。", // 使用できない（APIでは発生しないかも）
  22005: "正しいクーポンコードを入力してください。", // 有効期限切れ
};

export const VOUCHER_CODE_CHECK_ERROR_REASONS = {
  invalid_now: {
    message: "正しいクーポンコードを入力してください。",
    code: "22001-1",
  },
  over_max_number_of_granted_peruser: {
    message: "このクーポンコードは使用済みです。",
    code: "22001-2",
  },
  over_max_number_of_granted_overall: {
    message: "このクーポンコードは使用済みです。",
    code: "22001-3",
  },
  expired: {
    message: "正しいクーポンコードを入力してください。",
    code: "22001-4",
  },
  not_for_product: {
    message: "このクーポンコードは商品購入に使用できません。",
    code: "22001-5",
  },
  no_target_product: {
    message: "クーポンが適用できない商品です。",
    code: "22001-6",
  },
  not_for_course: {
    message: "このクーポンコードは月額商品の購入には使用できません。",
    code: "22001-7",
  },
  no_target_course: {
    message: "クーポンが使用できない月額商品です。",
    code: "22001-8",
  },
  not_available: {
    message: "トライアルが終了しているため適用できません。",
    code: "22001-9",
  },
  over_the_price_limit: {
    message: "適用できないクーポンです。",
    code: "22001-10",
  },
};

export const UNSUPPORTED_SCHEME_ERROR_MESSAGE = {
  99999: "A network request was made using an unsupported URI scheme.（ブラウザエラー。SPOOXサーバーダウンの可能性あり）",
};
